import type { MeResponse } from '@akpl/shared';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { api } from '../api';
import { useAuth } from '../auth';

export const ME_QUERY_KEY = ['me'] as const;

/**
 * `GET /me` (docs/03 §2, docs/05 §1) — the caller's profile, effective features and active
 * session. Keyed by uid so switching accounts (guest → account) never serves the previous
 * user's answer from the cache, and disabled while signed out because the route needs a token.
 *
 * This is also where the server-side user upsert happens: the API creates the `users` row and
 * redeems pending grants on the first authenticated call (docs/05 §2 step 3), so a freshly
 * signed-up phone must call `/me` before it can rely on `features`.
 */
export function useMe(): UseQueryResult<MeResponse> {
  const auth = useAuth();
  const uid = auth.user?.uid ?? null;
  return useQuery({
    queryKey: [...ME_QUERY_KEY, uid],
    queryFn: () => api.me(),
    enabled: Boolean(uid),
    staleTime: 30_000,
  });
}

/** Invalidates `/me` after anything that can change features or the active session. */
export function useRefreshMe(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
  };
}
